import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'

import { useDispatch } from 'APP/Hooks/redux'
import { useGetBooking } from 'APP/Hooks/useGetBooking'
import AppointmentBookingRedux from 'APP/Redux/AppointmentBookingRedux'
import { providerBookingActions } from 'APP/Store/ProviderBooking'
import { logger } from 'APP/Lib/Datadog'

import { ProviderAppointmentBooking } from '../ProviderBased'
import TimeAppointmentBooking from '../TimeBased'
import { LoadingIndicator } from '../common/loadingIndicator'
import ExpiredSchedule from '../common/expiredSchedule'
import { useProviderSelection } from '../ProviderBased/useProviderSelection'
import { deriveBookingProps } from './deriveBookingProps'
import { clampDateFromToNow } from './clampDateFromToNow'
import type {
  AppointmentBookingBaseComponentProps,
  AppointmentBookingExplicitProps,
  BookingFlowProps,
} from './types'

const hasBookingId = (
  props: AppointmentBookingExplicitProps
): props is AppointmentBookingExplicitProps & { booking_id: string } => !!props.booking_id

/**
 * Base appointment booking view.
 *
 * Reusable from any entry point (no dependency on chat flow).
 */
export const AppointmentBookingBase = (props: AppointmentBookingBaseComponentProps) => {
  const dispatch = useDispatch()
  const {
    booking,
    loading: isLoadingBooking,
    error: bookingError,
    isReady: isBookingReady,
  } = useGetBooking(props.booking_id)

  // Pinned so re-renders and booking updates don't shift date_from and reset the flow.
  const [openedAt] = useState(() => moment())
  const isDeriveMode = props.derive_from_booking
  const derivedProps = useMemo(
    () =>
      (isDeriveMode
        ? { ...props, ...deriveBookingProps(booking) }
        : props) as AppointmentBookingExplicitProps,
    [isDeriveMode, props, booking]
  )

  const resolvedProps = useMemo(() => {
    const { hide_past_days, date_from, date_until } = derivedProps
    // The dates are undefined until the booking loads in derive mode, despite the cast above.
    if (!hide_past_days || !date_from || !date_until) {
      return derivedProps
    }
    return { ...derivedProps, date_from: clampDateFromToNow(date_from, date_until, openedAt) }
  }, [derivedProps, openedAt])

  const { hasProviderSelection, showProviderSelection, isLoadingTypes, hasFetchedTypes } =
    useProviderSelection(resolvedProps.appointment_type)

  useEffect(() => {
    return () => {
      dispatch(AppointmentBookingRedux.resetState())
      dispatch(providerBookingActions.resetState())
    }
  }, [dispatch])

  // Single /availabilities/exists call over the whole booking window, for the day-tab dots.
  useEffect(() => {
    // Without a booking id, the expired screen shows instead of a flow.
    if (props.booking_id) {
      dispatch(AppointmentBookingRedux.getAvailabilitiesExists({ bookingId: props.booking_id }))
    }
  }, [props.booking_id, dispatch])

  useEffect(() => {
    if (!props.booking_id) {
      logger.error('Appointment booking not opened: Missing bookingId', {
        appointment_type: resolvedProps.appointment_type,
      })
    }
  }, [props.booking_id, resolvedProps.appointment_type])

  useEffect(() => {
    if (hasProviderSelection) {
      dispatch(providerBookingActions.getAppointmentTypes())
    }
  }, [hasProviderSelection, dispatch])

  const flowProps = useMemo<BookingFlowProps | null>(
    () =>
      hasBookingId(resolvedProps)
        ? { ...resolvedProps, booking, isLoadingBooking, isBookingReady }
        : null,
    [resolvedProps, booking, isLoadingBooking, isBookingReady]
  )

  const isDeriveModeLoading =
    isDeriveMode && (isLoadingBooking || (!bookingError && !isBookingReady))
  const showLoading =
    isDeriveModeLoading || isLoadingTypes || (hasProviderSelection && !hasFetchedTypes)
  if (showLoading) {
    return <LoadingIndicator />
  }

  if ((isDeriveMode && bookingError) || !flowProps) {
    return <ExpiredSchedule quitScheduling={() => props.close?.()} />
  }

  return showProviderSelection ? (
    <ProviderAppointmentBooking {...flowProps} />
  ) : (
    <TimeAppointmentBooking {...flowProps} />
  )
}
